import React, { useContext, useEffect, useState } from 'react';
import { Box, Chip, Stack, Typography, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import { Add } from '@mui/icons-material';
import TableComponent, { IColumnProps } from '../../../components/TableComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import ModalComponent from '../../../components/ModalComponent';
import TextFieldComponent from '../../../components/TextFieldComponent';
import SwitchComponent from '../../../components/SwitchComponent';
import DropdownSearchbleComponent from '../../../components/DropdownSearchbleComponent';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useGetAllHomeAssistantSchedulesByLicenseAsync,
  useCreateHomeAssistantScheduleAsync,
  useUpdateHomeAssistantScheduleAsync,
  useDeleteHomeAssistantScheduleAsync,
} from '../../../services/Devices/HomeAssistantScheduleService/hooks/useMutation';
import {
  HomeAssistantScheduleWeekday,
  IHomeAssistantScheduleProps,
} from '../../../services/Devices/HomeAssistantScheduleService/types';
import { useGetAllHomeAssistantDevicesByLicenseAsync } from '../../../services/Devices/HomeAssistantDeviceService/hooks/useMutation';
import { IHomeAssistantDeviceProps } from '../../../services/Devices/HomeAssistantDeviceService/types';
import { useGetAllDeviceGroupByLicenseAsync } from '../../../services/DeviceGroupService/hooks/useMutation';
import { IDeviceGroupSimpleProps } from '../../../services/DeviceGroupService/types';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';

const WEEKDAYS: { value: HomeAssistantScheduleWeekday; label: string }[] = [
  { value: 'mon', label: 'Seg' },
  { value: 'tue', label: 'Ter' },
  { value: 'wed', label: 'Qua' },
  { value: 'thu', label: 'Qui' },
  { value: 'fri', label: 'Sex' },
  { value: 'sat', label: 'Sáb' },
  { value: 'sun', label: 'Dom' },
];

type TargetType = 'DEVICE' | 'GROUP';

const ACCENT = '#38bdf8';

const toggleButtonSx = {
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.2)',
  '&.Mui-selected': {
    color: '#fff',
    backgroundColor: alpha(ACCENT, 0.25),
    '&:hover': { backgroundColor: alpha(ACCENT, 0.35) },
  },
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
};

const SchedulesTab = () => {
  const { showSnackbar } = useContext(AppContext);

  const [schedules, setSchedules] = useState<IHomeAssistantScheduleProps[]>([]);
  const [devices, setDevices] = useState<IHomeAssistantDeviceProps[]>([]);
  const [groups, setGroups] = useState<IDeviceGroupSimpleProps[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<IHomeAssistantScheduleProps>();

  const [targetType, setTargetType] = useState<TargetType>('DEVICE');
  const [deviceId, setDeviceId] = useState<number>();
  const [deviceGroupId, setDeviceGroupId] = useState<number>();
  const [service, setService] = useState<'turn_on' | 'turn_off'>('turn_on');
  const [timeOfDay, setTimeOfDay] = useState('08:00');
  const [daysOfWeek, setDaysOfWeek] = useState<HomeAssistantScheduleWeekday[]>([]);
  const [isActive, setIsActive] = useState(true);

  const { mutateAsync: mutateGetSchedules, isLoading: isLoadingSchedules } =
    useGetAllHomeAssistantSchedulesByLicenseAsync();
  const { mutateAsync: mutateGetDevices } = useGetAllHomeAssistantDevicesByLicenseAsync();
  const { mutateAsync: mutateGetGroups } = useGetAllDeviceGroupByLicenseAsync();
  const { mutateAsync: mutateCreate } = useCreateHomeAssistantScheduleAsync();
  const { mutateAsync: mutateUpdate } = useUpdateHomeAssistantScheduleAsync();
  const { mutateAsync: mutateDelete } = useDeleteHomeAssistantScheduleAsync();

  const reload = () => {
    mutateGetSchedules().then(setSchedules);
    mutateGetDevices().then(setDevices);
    mutateGetGroups().then(setGroups);
  };

  useEffect(() => {
    reload();
  }, []);

  const resetForm = () => {
    setSelectedSchedule(undefined);
    setTargetType('DEVICE');
    setDeviceId(undefined);
    setDeviceGroupId(undefined);
    setService('turn_on');
    setTimeOfDay('08:00');
    setDaysOfWeek([]);
    setIsActive(true);
    setIsModalOpen(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const timeOfDayFull = timeOfDay.length === 5 ? `${timeOfDay}:00` : timeOfDay;

    if (selectedSchedule) {
      await mutateUpdate({
        id: selectedSchedule.id,
        data: { service, timeOfDay: timeOfDayFull, daysOfWeek, isActive },
      });
      showSnackbar('Agendamento atualizado com sucesso.', 'success');
    } else {
      await mutateCreate({
        deviceId: targetType === 'DEVICE' ? deviceId : undefined,
        deviceGroupId: targetType === 'GROUP' ? deviceGroupId : undefined,
        service,
        timeOfDay: timeOfDayFull,
        daysOfWeek,
        isActive,
      });
      showSnackbar('Agendamento criado e enviado ao Home Assistant.', 'success');
    }
    resetForm();
    reload();
  };

  const handleConfirmDelete = async () => {
    if (!selectedSchedule) return;
    await mutateDelete(selectedSchedule.id);
    showSnackbar('Agendamento removido.', 'success');
    setIsDeleteOpen(false);
    setSelectedSchedule(undefined);
    reload();
  };

  const targetLabel = (schedule: IHomeAssistantScheduleProps) => {
    if (schedule.deviceId) {
      return devices.find((d) => d.id === schedule.deviceId)?.name ?? `Device #${schedule.deviceId}`;
    }
    if (schedule.deviceGroupId) {
      return `Grupo: ${groups.find((g) => g.id === schedule.deviceGroupId)?.name ?? schedule.deviceGroupId}`;
    }
    return '-';
  };

  const columns: IColumnProps[] = [
    {
      id: 'target',
      label: 'Alvo',
      width: '25%',
      render: (_val, _idx, row?: IHomeAssistantScheduleProps) => <>{row && targetLabel(row)}</>,
    },
    {
      id: 'service',
      label: 'Ação',
      width: '15%',
      render: (_val, _idx, row?: IHomeAssistantScheduleProps) => (
        <>{row?.service === 'turn_on' ? 'Ligar' : 'Desligar'}</>
      ),
    },
    { id: 'timeOfDay', label: 'Horário', width: '15%' },
    {
      id: 'daysOfWeek',
      label: 'Dias',
      width: '25%',
      render: (_val, _idx, row?: IHomeAssistantScheduleProps) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {row?.daysOfWeek.map((d) => (
            <Chip
              key={d}
              size="small"
              label={WEEKDAYS.find((w) => w.value === d)?.label ?? d}
            />
          ))}
        </Stack>
      ),
    },
    {
      id: 'actions',
      label: 'Ações',
      align: 'center',
      width: '20%',
      render: (_val, _idx, row?: IHomeAssistantScheduleProps) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <ButtonComponent
            variant="text"
            size="small"
            onClick={() => {
              if (!row) return;
              setSelectedSchedule(row);
              setTargetType(row.deviceId ? 'DEVICE' : 'GROUP');
              setDeviceId(row.deviceId);
              setDeviceGroupId(row.deviceGroupId);
              setService(row.service);
              setTimeOfDay(row.timeOfDay.slice(0, 5));
              setDaysOfWeek(row.daysOfWeek);
              setIsActive(row.isActive);
              setIsModalOpen(true);
            }}
          >
            Editar
          </ButtonComponent>
          <ButtonComponent
            variant="text"
            color="error"
            size="small"
            onClick={() => {
              if (row) {
                setSelectedSchedule(row);
                setIsDeleteOpen(true);
              }
            }}
          >
            Excluir
          </ButtonComponent>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Agendamentos</Typography>
        <ButtonComponent variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
          Novo agendamento
        </ButtonComponent>
      </Box>
      <Typography variant="body2" sx={{ color: 'gray', mb: 2 }}>
        Agendamentos recorrentes são enviados como automação para o Home Assistant — quem
        executa é o próprio Home Assistant, no horário configurado.
      </Typography>

      <TableComponent
        rows={schedules}
        columns={columns}
        isLoading={isLoadingSchedules}
        emptyMessage="Nenhum agendamento cadastrado ainda."
      />

      <ModalComponent
        isOpen={isModalOpen}
        onClose={resetForm}
        title={selectedSchedule ? 'Editar agendamento' : 'Novo agendamento'}
      >
        <Box sx={{ minWidth: 360 }}>
          {!selectedSchedule && (
            <ToggleButtonGroup
              value={targetType}
              exclusive
              onChange={(_e, val) => val && setTargetType(val)}
              sx={{ mb: 2 }}
              fullWidth
            >
              <ToggleButton value="DEVICE" sx={toggleButtonSx}>Dispositivo</ToggleButton>
              <ToggleButton value="GROUP" sx={toggleButtonSx}>Grupo</ToggleButton>
            </ToggleButtonGroup>
          )}

          {!selectedSchedule && targetType === 'DEVICE' && (
            <DropdownSearchbleComponent<IHomeAssistantDeviceProps, number>
              label="Dispositivo"
              value={deviceId}
              labelField={'name'}
              valueField={'id'}
              options={devices}
              onChange={(v) => setDeviceId(v ?? undefined)}
            />
          )}
          {!selectedSchedule && targetType === 'GROUP' && (
            <DropdownSearchbleComponent<IDeviceGroupSimpleProps, number>
              label="Grupo"
              value={deviceGroupId}
              labelField={'name'}
              valueField={'id'}
              options={groups}
              onChange={(v) => setDeviceGroupId(v ?? undefined)}
            />
          )}

          <ToggleButtonGroup
            value={service}
            exclusive
            onChange={(_e, val) => val && setService(val)}
            sx={{ mb: 2, mt: 2 }}
            fullWidth
          >
            <ToggleButton value="turn_on" sx={toggleButtonSx}>Ligar</ToggleButton>
            <ToggleButton value="turn_off" sx={toggleButtonSx}>Desligar</ToggleButton>
          </ToggleButtonGroup>

          <TextFieldComponent
            label="Horário"
            type="time"
            value={timeOfDay}
            onChangeText={setTimeOfDay}
            fullWidth
            sx={{ mb: 2 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Typography variant="body2" sx={{ mb: 1 }}>
            Dias da semana
          </Typography>
          <ToggleButtonGroup
            value={daysOfWeek}
            onChange={(_e, val) => setDaysOfWeek(val)}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            {WEEKDAYS.map((day) => (
              <ToggleButton key={day.value} value={day.value} sx={toggleButtonSx}>
                {day.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box sx={{ mb: 2 }}>
            <SwitchComponent label="Ativo" value={isActive} onChange={setIsActive} color="primary" />
          </Box>

          <ButtonComponent
            variant="contained"
            fullWidth
            disabled={
              !timeOfDay ||
              daysOfWeek.length === 0 ||
              (!selectedSchedule &&
                ((targetType === 'DEVICE' && !deviceId) ||
                  (targetType === 'GROUP' && !deviceGroupId)))
            }
            onClick={handleSave}
          >
            Salvar
          </ButtonComponent>
        </Box>
      </ModalComponent>

      <ConfirmDeleteModalComponent
        isVisibleDeleteDialog={isDeleteOpen}
        setIsVisibleDeleteDialog={setIsDeleteOpen}
        title="Excluir agendamento"
        message="Tem certeza que deseja excluir este agendamento? Ele também será removido do Home Assistant."
        handleCancelDeletion={() => {
          setIsDeleteOpen(false);
          setSelectedSchedule(undefined);
        }}
        handleConfirmDeletion={handleConfirmDelete}
      />
    </Box>
  );
};

export default SchedulesTab;
